'use client';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cn from 'classnames';
import {
    breakpointLarge,
    breakpointMedium,
    breakpointSmall,
} from 'config/styles/breakpoints';
import { brandWhite } from 'config/styles/colours';
import SSRLink from 'helpers/SSRLink';
import { FC } from 'react';
import ReactCountDown, { zeroPad } from 'react-countdown';
import css from 'styled-jsx/css';

/**
 * Props for `Countdown` slice.
 */
export type CountdownProps = SliceComponentProps<Content.CountdownSlice>;

const LinkableWrapper = ({
    applyLink = false,
    linkUrl,
    className,
    children,
}) => {
    return applyLink ? (
        <SSRLink
            linkType={'collections'}
            linkUrl={linkUrl}
            className={className}
            styles={{ textDecoration: `none` }}
        >
            {children}
        </SSRLink>
    ) : (
        children
    );
};

const CountdownItem = ({ block }) => {
    const {
        heading,
        font_colour,
        font_colour_mobile,
        mobile_image,
        desktop_image,
        background_colour,
        background_colour_mobile,
        countdown_timer,
        call_to_action_link,
    } = block;

    const splitCharacter = '^';
    const contentSplitPattern = new RegExp(/\^/i, 'ig');
    const headingMode =
        heading?.indexOf(splitCharacter) === -1 ? 'automatic' : 'manual';
    const formattedHeading = heading?.replace(contentSplitPattern, '\n');
    const hasImages = !!desktop_image?.url && !!mobile_image?.url;
    const applyLinkToWholeTileCondition = !!call_to_action_link?.url;

    const linkableWrapper = css.resolve`
        a {
            display: block;
        }
    `;

    const renderer = ({ days, hours, minutes, seconds, completed }) => {
        return (
            <div className="countdown">
                <div className="countdown_container">
                    <span className="countdown_time">{zeroPad(days)}</span>
                    <span className="countdown_time_text">DAYS</span>
                </div>
                <span className="colon">:</span>
                <div className="countdown_container">
                    <span className="countdown_time">{zeroPad(hours)}</span>
                    <span className="countdown_time_text">HOURS</span>
                </div>
                <span className="colon">:</span>
                <div className="countdown_container">
                    <span className="countdown_time">{zeroPad(minutes)}</span>
                    <span className="countdown_time_text">MINUTES</span>
                </div>
                <span className="colon">:</span>
                <div className="countdown_container">
                    <span className="countdown_time">{zeroPad(seconds)}</span>
                    <span className="countdown_time_text">SECONDS</span>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className={cn(`root monumentBlock_element`)}>
                <LinkableWrapper
                    applyLink={applyLinkToWholeTileCondition}
                    linkUrl={call_to_action_link?.url}
                    className={linkableWrapper.className}
                >
                    {desktop_image?.url && (
                        <img
                            src={desktop_image.url}
                            alt={
                                desktop_image?.alt ||
                                heading ||
                                'Countdown Image'
                            }
                            className="desktop_only"
                        />
                    )}
                    {mobile_image?.url && (
                        <img
                            src={mobile_image?.url}
                            alt={
                                mobile_image?.alt ||
                                heading ||
                                'Countdown Image'
                            }
                            className="mobile_only"
                        />
                    )}
                    <div
                        className={`${
                            hasImages ? 'overlay_content' : 'content_container'
                        }`}
                    >
                        <div className="countdown-content">
                            {formattedHeading && (
                                <div className="heading">
                                    {formattedHeading}
                                </div>
                            )}

                            {countdown_timer && (
                                <div className="countdown_timer">
                                    <ReactCountDown
                                        date={countdown_timer}
                                        renderer={renderer}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </LinkableWrapper>
            </div>
            <style jsx global>
                {`
                    .countdown {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                    }
                    .countdown_container {
                        display: flex;
                        flex-direction: column;
                        flex: 1;
                    }
                    .countdown_time {
                        font-size: 144px;
                        line-height: 1em;
                        font-family: TWK Everett Mono;
                    }
                    .countdown_time_text {
                        font-size: 16px;
                        line-height: 1.5em;
                        font-weight: 400;
                        font-family: TWK Everett;
                        margin-top: 24px;
                        text-align: center;
                    }
                    .colon {
                        font-size: 144px;
                        line-height: 1em;
                        font-family: TWK Everett Mono;
                    }
                    @media (max-width: ${breakpointLarge}) {
                        .countdown_time {
                            font-size: 72px;
                            line-height: 1.5em;
                            font-family: TWK Everett Mono;
                        }
                        .colon {
                            font-size: 72px;
                            line-height: 1em;
                            font-family: TWK Everett Mono;
                        }
                    }
                    @media (max-width: ${breakpointSmall}) {
                        .countdown_time {
                            font-size: 40px;
                            line-height: 48px;
                            font-family: TWK Everett Mono;
                        }
                        .countdown_time_text {
                            font-size: 12px;
                            line-height: 16px;
                            font-weight: 400;
                            font-family: TWK Everett;
                            text-align: center;
                            margin-top: 24px;
                        }
                        .colon {
                            font-size: 40px;
                            line-height: 48px;
                            font-family: TWK Everett Mono;
                        }
                    }
                    .countdown_timer {
                        width: 70%;
                        margin: auto !important;
                    }
                    @media (max-width: ${breakpointSmall}) {
                        .countdown_timer {
                            width: 100%;
                        }
                    }
                `}
            </style>
            <style jsx>
                {`
                    .mobile_only {
                        display: none;
                    }
                    .desktop_only {
                        display: block;
                    }
                    @media (max-width: ${breakpointSmall}) {
                        .mobile_only {
                            display: block;
                        }
                        .desktop_only {
                            display: none;
                        }
                    }
                    .countdown-content {
                        color: ${font_colour ? font_colour : brandWhite};
                        word-break: normal;

                        @media (max-width: ${breakpointSmall}) {
                            color: ${font_colour_mobile
                                ? font_colour_mobile
                                : brandWhite};
                        }

                        .heading {
                            text-align: center;
                            font-size: 32px;
                            margin: 0 auto 48px;
                            line-height: 1em;
                            ${headingMode === 'manual'
                                ? `white-space: pre-wrap`
                                : ''}

                            @media (max-width: ${breakpointSmall}) {
                                font-size: 12px;
                                line-height: 16px;
                                margin: 0 auto 32px;
                            }
                        }
                    }
                `}
            </style>
            <style jsx>
                {`
                    .monumentBlock_element {
                        flex: 1;
                        position: relative;
                        font-size: 16px; /* Reset the base font-size */
                        ${background_colour
                            ? `background-color: ${background_colour};`
                            : 'background-color: white;'}
                        @media (max-width: ${breakpointSmall}) {
                            ${background_colour_mobile
                                ? `background-color: ${background_colour_mobile};`
                                : 'background-color: white;'}
                        }
                    }
                    .overlay_content {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        box-sizing: border-box;
                        padding: 0 30px;
                    }

                    .content_container {
                        display: flex;
                        justify-content: center;
                        height: 100%;
                        width: 100%;
                        box-sizing: border-box;
                        padding: 232px 30px;

                        ${background_colour
                            ? `background-color: ${background_colour};`
                            : 'background-color: white;'};

                        @media (max-width: ${breakpointSmall}) {
                            ${background_colour_mobile
                                ? `background-color: ${background_colour_mobile};`
                                : 'background-color: white;'};

                            padding: 180px 30px;
                        }
                    }

                    // Defines the layout positioning

                    @media (max-width: ${breakpointSmall}) {
                        .heading {
                            font-weight: 600;
                        }
                    }
                `}
            </style>
        </>
    );
};

/**
 * Component for "Countdown" Slices.
 */
const Countdown: FC<CountdownProps> = ({ slice }) => {
    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            className="monumentBlock"
        >
            <CountdownItem block={slice.primary} />

            <style jsx global>
                {`
                    [data-siblings-count='1'] .content {
                        width: 40%;
                    }
                    [data-siblings-count='2'] .content {
                        width: 80%;
                    }
                    [data-siblings-count='1'] {
                        flex: 1 0 100%;
                    }
                    [data-siblings-count='2'] {
                        display: flex;
                        flex-direction: row;
                        width: 50%;
                    }
                    @media (max-width: ${breakpointLarge}) {
                        [data-siblings-count='1'] .content {
                            width: 70%;
                        }
                        [data-siblings-count='2'] .content {
                            width: 80%;
                        }
                    }
                    @media (max-width: ${breakpointSmall}) {
                        [data-siblings-count='1'] .content {
                            width: 100%;
                        }
                        [data-siblings-count='2'] .content {
                            width: 100%;
                        }
                        [data-siblings-count='2'] {
                            flex: 1 0 100%;
                        }
                    }
                    @media (max-width: ${breakpointMedium}) {
                        [data-siblings-count='2'] {
                            flex: 1 0 100%;
                        }
                    }
                `}
            </style>
            <style jsx>
                {`
                    .monumentBlock {
                        display: flex;
                    }
                    @media (max-width: ${breakpointMedium}) {
                        .monumentBlock {
                            flex-direction: column;
                        }
                    }
                `}
            </style>
        </section>
    );
};

export default Countdown;
